// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

import { useMemo } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './auth/AuthProvider';
import { CloudscapeStrings } from './lib/i18n';
import { NotifyProvider } from './lib/notify';
import { routes } from './router';

export function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: (count, err) =>
          count < 2 && (err as { status?: number }).status !== 403 && (err as { status?: number }).status !== 404,
        staleTime: 15_000,
        refetchOnWindowFocus: false,
      },
    },
  });
}

export function App() {
  const client = useMemo(() => makeQueryClient(), []);
  const router = useMemo(() => createBrowserRouter(routes), []);
  return (
    <CloudscapeStrings>
      <QueryClientProvider client={client}>
        <AuthProvider>
          <NotifyProvider>
            <RouterProvider router={router} />
          </NotifyProvider>
        </AuthProvider>
      </QueryClientProvider>
    </CloudscapeStrings>
  );
}
